import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { errorMessage, useToast } from '@/components/Toast';
import { Button } from '@/components/ui';
import { Icon } from '@/components/icons';

/**
 * Watch / unwatch one library authority, so a later change to it raises an alert.
 * Signed-in only — guests see nothing. Every toggle shares the
 * `['watches', 'source-ids']` query, so a list of them polls once.
 *
 * `variant="link"` is the compact inline form (chat source cards);
 * `variant="button"` is the ghost button used in card headers.
 */
export function WatchToggle({ sourceId, variant = 'link' }: { sourceId: string; variant?: 'link' | 'button' }) {
  const { status } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const watched = useQuery({
    queryKey: ['watches', 'source-ids'],
    queryFn: () => api.watches.sourceIds(),
    enabled: status === 'authenticated',
  });
  const isWatched = (watched.data ?? []).includes(sourceId);
  const toggle = useMutation({
    mutationFn: () => (isWatched ? api.watches.removeBySource(sourceId) : api.watches.create({ sourceId }).then(() => undefined)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['watches'] });
      toast.push(isWatched ? 'No longer watching this authority.' : 'Watching — you will be alerted if it changes.', 'ok');
    },
    onError: (e) => toast.push(errorMessage(e), 'danger'),
  });
  if (status !== 'authenticated') return null;

  if (variant === 'button') {
    return (
      <Button variant="ghost" size="sm" onClick={() => toggle.mutate()} disabled={toggle.isPending}
              aria-pressed={isWatched}
              title={isWatched ? 'Stop watching this authority' : 'Get alerted if this authority changes'}>
        <Icon.Bell /> {isWatched ? 'Watching' : 'Watch'}
      </Button>
    );
  }
  return (
    <button type="button" className="inline-link"
            onClick={() => toggle.mutate()} disabled={toggle.isPending} aria-pressed={isWatched}>
      {isWatched ? '★ Watching' : '☆ Watch'}
    </button>
  );
}
